import React from 'react';
import { Card, CardContent } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/features/shared/components/ui/tabs';
import {
  Bell, CheckCircle, AlertCircle, Info, Mail, Loader2, MessageSquare,
  Briefcase, CalendarDays, Receipt, Wallet, Award, GraduationCap, ClipboardCheck,
  Banknote, Target, ClipboardList, FileText, CalendarClock, Plug, Users, Paperclip, FileUp,
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useNotifications } from '@/features/hooks/useNotifications';
import { resolveNotificationLink } from '@/lib/notificationsApi';
import type { AppNotification } from '@/lib/notificationsApi';
import { notificationSubject, notificationTypeGlyph } from './notificationSubject';
import { formatDateTime } from '@/lib/orgFormat';
import { useOrgFormat } from '@/lib/useOrgFormat';

// Subject first: since the 2026-10-05 retyping, most notifications carry a
// real subject. Severities below are kept for rows stored BEFORE that, which
// still read "info"/"warning"/"success". Anything unmapped falls to the
// default bell, so a new type never breaks the row.
const SUBJECT_ICON: Record<string, React.ReactElement> = {
  work: <Briefcase className="w-5 h-5 text-blue-600" />,
  leave: <CalendarDays className="w-5 h-5 text-blue-600" />,
  expenses: <Receipt className="w-5 h-5 text-orange-600" />,
  payroll: <Wallet className="w-5 h-5 text-green-600" />,
  recognition: <Award className="w-5 h-5 text-orange-600" />,
  conversations: <MessageSquare className="w-5 h-5 text-orange-600" />,
  learning: <GraduationCap className="w-5 h-5 text-blue-600" />,
  approvals: <ClipboardCheck className="w-5 h-5 text-orange-600" />,
  finance: <Banknote className="w-5 h-5 text-green-600" />,
  performance: <Target className="w-5 h-5 text-blue-600" />,
  surveys: <ClipboardList className="w-5 h-5 text-blue-600" />,
  documents: <FileText className="w-5 h-5 text-blue-600" />,
  calendar: <CalendarClock className="w-5 h-5 text-blue-600" />,
  integrations: <Plug className="w-5 h-5 text-orange-600" />,
  people: <Users className="w-5 h-5 text-blue-600" />,
};

// Type-level glyphs (still badged and categorised by their subject).
const TYPE_GLYPH: Record<string, React.ReactElement> = {
  paperclip: <Paperclip className="w-5 h-5 text-info" />,
  'file-up': <FileUp className="w-5 h-5 text-info" />,
};

const getNotificationIcon = (type: string) => {
  const glyph = notificationTypeGlyph(type);
  if (glyph) return TYPE_GLYPH[glyph];
  const subject = notificationSubject(type);
  if (subject && SUBJECT_ICON[subject]) return SUBJECT_ICON[subject];
  switch (type) {
    case 'success':
    case 'welcome': return <CheckCircle className="w-5 h-5 text-green-600" />;
    case 'warning': return <AlertCircle className="w-5 h-5 text-orange-600" />;
    case 'info': return <Info className="w-5 h-5 text-blue-600" />;
    default: return <Bell className="w-5 h-5 text-muted-foreground" />;
  }
};

const SUBJECT_BADGE: Record<string, string> = {
  work: 'bg-blue-100 text-blue-800',
  leave: 'bg-blue-100 text-blue-800',
  expenses: 'bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800',
  payroll: 'bg-green-100 text-green-800',
  recognition: 'bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800',
  conversations: 'bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800',
  learning: 'bg-blue-100 text-blue-800',
  approvals: 'bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800',
  finance: 'bg-green-100 text-green-800',
  performance: 'bg-blue-100 text-blue-800',
  surveys: 'bg-blue-100 text-blue-800',
  documents: 'bg-blue-100 text-blue-800',
  calendar: 'bg-blue-100 text-blue-800',
  integrations: 'bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800',
  people: 'bg-blue-100 text-blue-800',
};

const getNotificationBadge = (type: string) => {
  const subject = notificationSubject(type);
  if (subject && SUBJECT_BADGE[subject]) return SUBJECT_BADGE[subject];
  switch (type) {
    case 'success':
    case 'welcome': return 'bg-green-100 text-green-800';
    case 'warning': return 'bg-orange-100 dark:bg-orange-500/[0.12] text-orange-800';
    case 'info': return 'bg-blue-100 text-blue-800';
    default: return 'bg-muted text-foreground';
  }
};

const NotificationCard: React.FC<{ n: AppNotification; onRead: (id: string) => void; onOpen: (n: AppNotification) => void }> = ({ n, onRead, onOpen }) => (
  <Card
    className={`cursor-pointer transition-colors ${!n.isRead ? 'border-l-4 border-l-orange-500 bg-orange-50/30 dark:bg-orange-500/10' : ''}`}
    onClick={() => { if (!n.isRead) onRead(n.id); onOpen(n); }}
  >
    <CardContent className="p-4">
      <div className="flex items-start space-x-3">
        {getNotificationIcon(n.type)}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-foreground">{n.title}</h3>
            <div className="flex items-center space-x-2">
              <Badge className={getNotificationBadge(n.type)}>{n.type}</Badge>
              <span className="text-sm text-muted-foreground">{formatDateTime(n.createdAt)}</span>
            </div>
          </div>
          {n.message && <p className="text-muted-foreground mt-1">{n.message}</p>}
          {!n.isRead && (
            <div className="flex items-center mt-2">
              <div className="w-2 h-2 bg-orange-500 rounded-full mr-2" />
              <span className="text-sm text-orange-600 font-medium">Unread</span>
            </div>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

export const NotificationsPage: React.FC = () => {
  useOrgFormat(); // re-render timestamps when the org date format loads/changes
  const { items, unread, loading, markRead, markAllRead } = useNotifications();
  const unreadItems = items.filter((n) => !n.isRead);
  const navigate = useNavigate();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  // A notification card OPENS what it's about (previously it only marked read
  // — every notification was a dead end on this page; audit 2026-09 §04).
  const openNotification = (n: AppNotification) => {
    const to = resolveNotificationLink(n.link, tenantSlug, n);
    if (to) navigate(to);
  };

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-h1 text-charcoal flex items-center gap-2">
              <Bell className="w-8 h-8" />
              Notifications
              {unread > 0 && <Badge className="bg-red-500 text-white">{unread}</Badge>}
            </h1>
            <p className="text-muted-foreground mt-1">Stay updated with important information and alerts</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => markAllRead()} disabled={unread === 0}>
              <Mail className="w-4 h-4 mr-2" />
              Mark All Read
            </Button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-orange-600">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          <span className="text-sm text-muted-foreground">Loading notifications…</span>
        </div>
      ) : (
        <Tabs defaultValue="all" className="space-y-6">
          <TabsList>
            <TabsTrigger value="all">All ({items.length})</TabsTrigger>
            <TabsTrigger value="unread">Unread ({unreadItems.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4">
            {items.length === 0 ? (
              <Card>
                <CardContent className="p-10 text-center text-muted-foreground">
                  <Bell className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm">No notifications yet. You'll see updates here as they happen.</p>
                </CardContent>
              </Card>
            ) : (
              items.map((n) => <NotificationCard key={n.id} n={n} onRead={markRead} onOpen={openNotification} />)
            )}
          </TabsContent>

          <TabsContent value="unread" className="space-y-4">
            {unreadItems.length === 0 ? (
              <Card>
                <CardContent className="p-10 text-center text-muted-foreground">
                  <CheckCircle className="w-10 h-10 text-green-300 mx-auto mb-3" />
                  <p className="text-sm">You're all caught up.</p>
                </CardContent>
              </Card>
            ) : (
              unreadItems.map((n) => <NotificationCard key={n.id} n={n} onRead={markRead} onOpen={openNotification} />)
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default NotificationsPage;
